/**
 * Manual Health Stats draft. Mirrors PeakApple `HealthForm` / `HealthDraft`
 * and PeakAndroid `HealthForm`: every metric is optional, at least one is
 * required, blank fields are omitted, and entered keys are locked in
 * `userFields` so a later wearable sync does not overwrite them.
 */

export interface ManualHealthFields {
  hrv: string;
  rhr: string;
  respRate: string;
  sleepHours: string;
  sleepMinutes: string;
}

export interface ManualHealthInitial {
  hrv?: number;
  rhr?: number;
  respRate?: number;
  sleepDuration?: number;
  userFields?: string[];
}

export interface ManualHealthDraft {
  hrv?: number;
  rhr?: number;
  respRate?: number;
  sleepDuration?: number;
  userFields: string[];
}

export interface ManualHealthValidation {
  sleepTooLong: boolean;
  fieldsInvalid: boolean;
  hasAnyValue: boolean;
  error: string | null;
  canSubmit: boolean;
  /** Null when this save would not change any metric. */
  draft: ManualHealthDraft | null;
}

const MAX_SLEEP_SECONDS = 16 * 3600;

export function vitalInputText(value?: number): string {
  if (value == null || !(value > 0)) return "";
  return String(Math.round(value));
}

export function roundRespRate(value: number): number {
  return Math.round(value * 10) / 10;
}

export function respRateInputText(value?: number): string {
  if (value == null || !(value > 0)) return "";
  return roundRespRate(value).toFixed(1);
}

export function sleepInput(seconds?: number): { hours: string; minutes: string } {
  if (seconds == null || !(seconds > 0)) return { hours: "", minutes: "" };
  return {
    hours: String(Math.floor(seconds / 3600)),
    minutes: String(Math.floor((seconds % 3600) / 60)),
  };
}

function parsePositiveInt(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n > 0 ? n : null;
}

function vitalInvalid(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return parsePositiveInt(trimmed) == null;
}

function changedVital(text: string, initial?: number): number | null {
  const trimmed = text.trim();
  if (trimmed === vitalInputText(initial)) return null;
  return parsePositiveInt(trimmed);
}

/** Positive value with at most one decimal place, or null. A trailing dot is allowed while typing. */
export function parseRespRate(text: string): number | null {
  const trimmed = text.trim().replace(",", ".");
  if (!trimmed) return null;
  const parts = trimmed.split(".");
  if (parts.length > 2) return null;
  if (parts.length === 2 && parts[1].length > 1) return null;
  if (!/^\d+$/.test(parts[0])) return null;
  if (parts.length === 2 && parts[1] !== "" && !/^\d$/.test(parts[1])) return null;
  const n = Number(trimmed);
  if (!(n > 0)) return null;
  return roundRespRate(n);
}

function respRateFieldInvalid(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return parseRespRate(trimmed) == null;
}

function changedRespRate(text: string, initial?: number): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const parsed = parseRespRate(trimmed);
  if (parsed == null) return null;
  if (initial != null && Math.abs(parsed - roundRespRate(initial)) < 0.001) {
    return null;
  }
  return parsed;
}

function componentInvalid(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return !/^\d+$/.test(trimmed);
}

function sleepSeconds(hoursText: string, minutesText: string): number | null {
  const hoursBlank = !hoursText.trim();
  const minutesBlank = !minutesText.trim();
  if (hoursBlank && minutesBlank) return null;
  const hours = /^\d+$/.test(hoursText.trim()) ? Number(hoursText.trim()) : 0;
  const minutes = /^\d+$/.test(minutesText.trim()) ? Number(minutesText.trim()) : 0;
  const total = hours * 3600 + minutes * 60;
  return total > 0 ? total : null;
}

export function validateManualHealthLog(
  fields: ManualHealthFields,
  initial?: ManualHealthInitial | null,
): ManualHealthValidation {
  const seconds = sleepSeconds(fields.sleepHours, fields.sleepMinutes);
  const sleepTooLong = (seconds ?? 0) > MAX_SLEEP_SECONDS;
  const fieldsInvalid =
    vitalInvalid(fields.hrv) ||
    vitalInvalid(fields.rhr) ||
    respRateFieldInvalid(fields.respRate) ||
    componentInvalid(fields.sleepHours) ||
    componentInvalid(fields.sleepMinutes);
  const hasAnyValue =
    changedVital(fields.hrv) != null ||
    changedVital(fields.rhr) != null ||
    parseRespRate(fields.respRate) != null ||
    seconds != null;

  const hrv = changedVital(fields.hrv, initial?.hrv);
  const rhr = changedVital(fields.rhr, initial?.rhr);
  const respRate = changedRespRate(fields.respRate, initial?.respRate);
  const sleepDuration =
    seconds != null && seconds !== initial?.sleepDuration ? seconds : null;

  const locked = new Set(initial?.userFields ?? []);
  const draft: ManualHealthDraft = { userFields: [] };
  if (hrv != null) {
    draft.hrv = hrv;
    locked.add("hrv");
  }
  if (rhr != null) {
    draft.rhr = rhr;
    locked.add("rhr");
  }
  if (respRate != null) {
    draft.respRate = respRate;
    locked.add("respRate");
  }
  if (sleepDuration != null) {
    draft.sleepDuration = sleepDuration;
    locked.add("sleepDuration");
  }
  draft.userFields = Array.from(locked).sort();

  const hasEntries =
    hrv != null || rhr != null || respRate != null || sleepDuration != null;

  const error = sleepTooLong
    ? "Sleep duration should not exceed 16 hours."
    : fieldsInvalid
      ? "HRV and resting heart rate are whole numbers. Respiratory rate can have one decimal."
      : null;

  return {
    sleepTooLong,
    fieldsInvalid,
    hasAnyValue,
    error,
    canSubmit: hasAnyValue && !sleepTooLong && !fieldsInvalid,
    draft: hasEntries ? draft : null,
  };
}
